import {Alert, Typography, Stack, Button, Tooltip} from '@mui/material';
import {useRegistrationStatusQuery} from "@features/home/regApi";
import {useAppSelector} from "@app/hooks";
import {RootState} from "@app/store";
import {Property} from "@features/home/types";
import {useCallback, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {getErrorMessage, U} from "@lib/utils";
import ConfirmDialog from '@features/Common/ConfirmDialog';
import { useAbandonPropertyMutation } from '@features/home/api';
import {FormikHelpers} from "formik";
import ExpandableSnack from "@features/Common/ExpandableSnack";
import {SnackType} from "@lib/types";
import {useSnackbar} from "notistack";

export const HomeRegistrationStatus = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const home: Property = useAppSelector((state: RootState) => state.property);
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const tenant = useAppSelector((state: RootState) => state.tenant);
  const currentTenant = useMemo(() => tenant.currentTenant || userTenant, [tenant.currentTenant, userTenant]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const hasAdminAccess = tenant.access ? tenant.access.includes('builder-admins') : false;

  const { data: isRegistered, isLoading, error } = useRegistrationStatusQuery({
    propertyId: home?.property_id,
    token: home?.token?.token_value,
  },
    {
      skip: !home?.property_id || !home?.token?.token_value
  });

  const handleProvision = useCallback(() => {
    navigate(`/homes/new/${U(home.property_id)}/provision`);
  },[home.property_id, navigate]);

  const [abandonProperty] = useAbandonPropertyMutation();

  const handleConfirm = useCallback(async (values: any, helpers: FormikHelpers<any>) => {
    if (!currentTenant?.tenant_id || !home?.property_id) {
      enqueueSnackbar("Cannot abandon property: Missing required data", {
        variant: "error",
      });
      return false;
    }

    return abandonProperty({
      userTenantId: currentTenant.tenant_id,
      propertyId: home.property_id,
    }).then((result: any) => {
      if ('error' in result) {
        enqueueSnackbar(`Failed to abandon ${home.name}:`, {
          key: "property-abandon-error",
          content: (
            <ExpandableSnack
              id="abandon-error"
              message={"Couldn't abandon home:"}
              variant={SnackType.error}
              detail={getErrorMessage(result.error)}
            />),
        });
        return false;
      } else {
        enqueueSnackbar(`${home.name} abandoned successfully`, {
          variant: "success",
        });
        navigate('/');
        return true;
      }
    });
  }, [currentTenant.tenant_id, home.property_id, home.name, abandonProperty, enqueueSnackbar, navigate]);


  // Don't show anything while loading or if there's an error
  if (isLoading || error || isRegistered === undefined) {
    return <></>;
  }

  // Only show the alert if the home is not registered
  if (!isRegistered) {
    return (
      <>
        <Alert
          severity="warning"
          variant='outlined'
          sx={{
            width: '100%',
            background: 'white',
            margin: 1,
            alignItems: "center",
            gap: 2,
          }}
        >
          <Stack
            sx={{ width: '100%' }}
          >
            <Typography>This home is not registered.</Typography>
            {
              hasAdminAccess && (
                <Stack direction="row" alignItems="center">
                  <Typography>You can</Typography>
                  <Tooltip title="Provision this home with devices" placement="top" arrow>
                    <span>
                      <Button onClick={handleProvision} color="success">Provision</Button>
                    </span>
                  </Tooltip>
                  <Typography>or</Typography>
                  <Tooltip title="Abandon this home and remove it from all views" placement="top" arrow>
                    <span>
                      <Button
                          onClick={() => setIsDialogOpen(true)}
                          color="error"
                          aria-label="Abandon home"
                      >
                          abandon
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography>it.</Typography>
              </Stack>
            )}
          </Stack>
        </Alert>
        <ConfirmDialog
          title="Confirm Home Abandonment"
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={handleConfirm}
          positive="Yes, I understand"
          negative="No"
          initialValues={{}}>
          Are you sure you want to abandon this home? Once confirmed, this home will be removed from your view.
        </ConfirmDialog>
    </>
    );
  }

  // If home is registered, don't show anything
  return <></>;
};
